"use client";

import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Modal } from "~/components/Modal";
import {
    assetLabel,
    assetSnippet,
    AssetLogo,
    formatShortTime,
    isToday,
    SparkleAvatar,
    TickerBadge,
} from "./shared";

// // // //

export type DockConversation = {
    id: string;
    assetID: string;
    title: string | null;
    updatedAt: Date | string;
};

/**
 * ConversationList
 *
 * The dock's saved-chats view (design stories `empty` + `list`). Renders a
 * grounded empty state, a loading skeleton, an error retry, or the threads
 * bucketed into Today / Earlier with a monospace ticker, relative time, title
 * and asset blurb. `dense` drops the blurb and tightens padding for the
 * maximized modal's left rail.
 */
export function ConversationList(props: {
    isLoading: boolean;
    isError: boolean;
    conversations: DockConversation[];
    deletingId: string | undefined;
    onOpen: (assetID: string) => void;
    onDelete: (id: string) => void;
    onNewChat: () => void;
    onRetry: () => void;
    dense?: boolean;
}) {
    const {
        isLoading,
        isError,
        conversations,
        deletingId,
        onOpen,
        onDelete,
        onNewChat,
        onRetry,
        dense = false,
    } = props;

    if (isLoading) {
        return <ConversationListSkeleton dense={dense} />;
    }

    if (isError) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
                <p className="text-sm text-secondary-foreground">
                    Couldn&apos;t load your chats.
                </p>
                <Button variant="outline" size="sm" onClick={onRetry}>
                    Try again
                </Button>
            </div>
        );
    }

    if (conversations.length === 0) {
        return <ConversationListEmpty onNewChat={onNewChat} dense={dense} />;
    }

    const today = conversations.filter((c) => isToday(c.updatedAt));
    const earlier = conversations.filter((c) => !isToday(c.updatedAt));

    return (
        <div
            className={cn(
                "h-full overflow-y-auto py-2",
                dense ? "px-2" : "px-2.5",
            )}
        >
            <ConversationGroup
                label="Today"
                conversations={today}
                active
                deletingId={deletingId}
                onOpen={onOpen}
                onDelete={onDelete}
                dense={dense}
            />
            <ConversationGroup
                label="Earlier"
                conversations={earlier}
                deletingId={deletingId}
                onOpen={onOpen}
                onDelete={onDelete}
                dense={dense}
            />
        </div>
    );
}

// // // //

function ConversationGroup(props: {
    label: string;
    conversations: DockConversation[];
    active?: boolean;
    deletingId: string | undefined;
    onOpen: (assetID: string) => void;
    onDelete: (id: string) => void;
    dense: boolean;
}) {
    if (props.conversations.length === 0) {
        return null;
    }

    return (
        <>
            <div className="px-2.5 pt-3 pb-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                {props.label}
            </div>
            <ul className="space-y-0.5">
                {props.conversations.map((conversation) => (
                    <ConversationRow
                        key={conversation.id}
                        conversation={conversation}
                        active={props.active}
                        isDeleting={props.deletingId === conversation.id}
                        onOpen={props.onOpen}
                        onDelete={props.onDelete}
                        dense={props.dense}
                    />
                ))}
            </ul>
        </>
    );
}

function ConversationRow(props: {
    conversation: DockConversation;
    active?: boolean;
    isDeleting: boolean;
    onOpen: (assetID: string) => void;
    onDelete: (id: string) => void;
    dense: boolean;
}) {
    const { conversation, active, isDeleting, onOpen, onDelete, dense } = props;
    const snippet = assetSnippet(conversation.assetID);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const chatTitle = conversation.title || assetLabel(conversation.assetID);

    return (
        <li className="group/row relative">
            <button
                type="button"
                onClick={() => onOpen(conversation.assetID)}
                className={cn(
                    "block w-full rounded-xl text-left transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                    dense ? "px-2.5 py-2.5" : "px-3 py-2.5",
                )}
            >
                <div className="mb-1 flex items-center gap-2">
                    <AssetLogo assetID={conversation.assetID} />
                    <TickerBadge
                        ticker={conversation.assetID}
                        active={active}
                    />
                    <span className="ml-auto text-[11px] text-muted-foreground transition-opacity group-hover/row:opacity-0">
                        {formatShortTime(conversation.updatedAt)}
                    </span>
                </div>
                <div className="truncate text-[13.5px] font-medium text-foreground">
                    {chatTitle}
                </div>
                {!dense && snippet && (
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {snippet}
                    </div>
                )}
            </button>
            <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={isDeleting}
                aria-label="Delete chat"
                title="Delete chat"
                className="absolute top-2.5 right-2.5 rounded-md p-1 text-muted-foreground opacity-0 transition hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50 group-hover/row:opacity-100"
            >
                {isDeleting ? (
                    <Loader2 className="size-3.5 animate-spin" />
                ) : (
                    <Trash2 className="size-3.5" />
                )}
            </button>

            <Modal
                show={confirmOpen}
                size="sm"
                onHide={() => setConfirmOpen(false)}
                allowClose={!isDeleting}
            >
                <div className="w-full bg-card p-6 text-left">
                    <h3
                        id="modal-headline"
                        className="text-lg font-semibold text-foreground"
                    >
                        Delete chat
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                        This conversation will be removed and can&apos;t be
                        recovered.
                    </p>
                    <div className="mt-6 flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isDeleting}
                            onClick={() => setConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={isDeleting}
                            onClick={() => {
                                onDelete(conversation.id);
                                setConfirmOpen(false);
                            }}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </li>
    );
}

// // // //

function ConversationListEmpty(props: {
    onNewChat: () => void;
    dense: boolean;
}) {
    if (props.dense) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <SparkleAvatar className="size-11" />
                <p className="text-sm font-medium text-foreground">
                    No chats yet
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={props.onNewChat}
                    className="gap-1.5 rounded-lg border-muted text-primary hover:border-primary-light hover:bg-primary-light/30 hover:text-primary"
                >
                    <Plus aria-hidden="true" />
                    New chat
                </Button>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col items-center justify-center gap-5 px-8 py-12 text-center">
            <SparkleAvatar className="size-13" glyphClassName="size-6" />
            <div className="space-y-2">
                <p className="text-base font-semibold text-foreground">
                    No chats yet
                </p>
                <p className="mx-auto max-w-[17rem] text-sm leading-relaxed text-tertiary-foreground">
                    Ask AI about any asset&apos;s data room — answers are
                    grounded in the real documents.
                </p>
            </div>
            <Button
                onClick={props.onNewChat}
                className="h-9 gap-1.5 rounded-xl px-4 text-sm font-semibold shadow-lg shadow-primary/25 hover:bg-primary-dark"
            >
                <Plus aria-hidden="true" />
                New chat
            </Button>
        </div>
    );
}

/** Placeholder rows shown while conversations load — steadier than a spinner. */
function ConversationListSkeleton(props: { dense: boolean }) {
    return (
        <div
            className={cn("space-y-0.5 py-2", props.dense ? "px-2" : "px-2.5")}
            aria-hidden="true"
        >
            <div className="px-2.5 pt-3 pb-1.5">
                <span className="block h-2.5 w-12 animate-pulse rounded bg-muted" />
            </div>
            {Array.from({ length: props.dense ? 5 : 6 }).map((_, index) => (
                <div
                    key={index}
                    className={cn(
                        "flex flex-col gap-2",
                        props.dense ? "px-2.5 py-2.5" : "px-3 py-2.5",
                    )}
                >
                    <div className="flex items-center gap-2">
                        <span className="size-5 animate-pulse rounded-md bg-muted" />
                        <span className="h-5 w-14 animate-pulse rounded-md bg-muted" />
                        <span className="ml-auto h-2.5 w-6 animate-pulse rounded bg-muted/70" />
                    </div>
                    <span className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                    {!props.dense && (
                        <span className="h-2.5 w-4/5 animate-pulse rounded bg-muted/70" />
                    )}
                </div>
            ))}
        </div>
    );
}
